import React from 'react';
import Box from '@mui/material/Box';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import Input from '../Input';
import CascadeSelectOptions from '../CascadeSelectOptions';
import styles from './CascadeSelect.module.scss';

export interface Option {
  id: string;
  name: string;
  pinyin?: string;
  searchPinyin?: string;
}

export interface CascadeOption extends Option {
  disabled?: boolean;
  children?: CascadeOption[];
}

interface Props<T> {
  label?: string;
  subLabel?: string;
  value?: T;
  error?: boolean;
  helperText?: string | React.ReactNode;
  disabled?: boolean;
  sx?: React.CSSProperties;
  inputSx?: React.CSSProperties;
  className?: string;
  options?: CascadeOption[];
  placeholder?: string;
  separatorIcon?: string;
  isHalfWidth?: boolean;
  isNeedSeparator?: boolean;
  labelColor?: string;
  uniqueId?: string | number;
  onChange: (value: string, selectOptions: Option[]) => void;
  onFocus?: () => void;
}

export const getSelectedOptions = (
  options: CascadeOption[],
  selectedValue: string,
): Omit<CascadeOption, 'children'>[] => {
  let selectedOptions: CascadeOption[] = [];
  const search = (tempOptions: CascadeOption[], ref: CascadeOption[] = []) => {
    tempOptions.forEach((option) => {
      const { children, name, id, ...restOption } = option;
      if (id === selectedValue) {
        selectedOptions = [
          ...ref,
          {
            ...restOption,
            id,
            name,
          },
        ];
      } else if (Array.isArray(children) && children.length >= 1) {
        search(children, [
          ...ref,
          {
            ...restOption,
            id,
            name,
          },
        ]);
      }
    });
  };
  search(options);
  return selectedOptions;
};

const CascadeSelect = (props: Props<any>) => {
  const {
    value,
    label,
    subLabel,
    error,
    helperText,
    disabled,
    sx,
    inputSx,
    className = '',
    labelColor,
    uniqueId = '',
    options = [],
    placeholder,
    separatorIcon = ' > ',
    onChange,
    isHalfWidth = false,
    isNeedSeparator = true,
    onFocus,
  } = props;
  const [endAdornment, setEndAdornment] = React.useState(
    <ArrowDropDownIcon sx={{ color: '#79747e' }} />,
  );
  const dropdownMenuRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement | undefined>(undefined);

  const getValue = (): string => {
    if (value && Array.isArray(value) && !value.some((option) => !option)) {
      return value.map((option) => option.name).join(separatorIcon);
    } else if (typeof value === 'string') {
      const selectedOptions = getSelectedOptions(options, value as string);
      return isNeedSeparator
        ? selectedOptions.map((option) => option.name).join(separatorIcon)
        : selectedOptions[selectedOptions.length - 1].name;
    }
    return '';
  };

  const handleClick = (): void => {
    if (disabled) return;
    dropdownMenuRef.current?.classList.add(styles.show);
  };

  const handleSelect = (option: CascadeOption): void => {
    const selectedOptions = getSelectedOptions(options, option.id);
    onChange(selectedOptions.slice(-1)[0].id, selectedOptions as Option[]);
    hideDropdownMenu();
    inputRef?.current?.blur();
  };

  const hideDropdownMenu = (): void => {
    dropdownMenuRef.current?.classList.remove(styles.show);
    setEndAdornment(<ArrowDropDownIcon sx={{ color: '#79747e' }} />);
  };

  return (
    <Box aria-hidden className={`${className} inline-block relative`} sx={sx}>
      <Input
        autoComplete="off"
        label={label}
        subLabel={subLabel}
        labelColor={labelColor}
        placeholder={placeholder}
        inputSx={inputSx}
        error={error}
        value={getValue()}
        helperText={helperText}
        disabled={disabled}
        endAdornment={endAdornment}
        handleOnFocus={() => {
          handleClick();
          onFocus?.();
          setEndAdornment(<ArrowDropUpIcon sx={{ color: '#79747e' }} />);
        }}
        handleOnBlur={hideDropdownMenu}
        myInputRef={inputRef}
        readOnly
      />
      <CascadeSelectOptions
        options={options}
        isParentRef={true}
        handleSelect={handleSelect}
        isHalfWidth={isHalfWidth}
        dropdownMenuRef={dropdownMenuRef}
        pageUniqueId={uniqueId.toString()}
      />
    </Box>
  );
};

export default CascadeSelect;
